<script setup lang="ts">
import { computed } from 'vue'
import type { IGraphTarget } from '@/modules/dashboard/types/graph/IGraphTarget'
import type { IListItem } from '@/modules/dashboard/types/graph/IListItem'
import type { ITableWidget } from '@/modules/dashboard/types/graph/ITableWidget'

interface Props {
  widgetData: ITableWidget
  currentTarget?: IGraphTarget
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
})

const datasetLabel = computed(() => props.widgetData.data.datasets?.[0]?.label || '')

const allItems = computed<IListItem[]>(() => {
  const labels = props.widgetData.data.labels || []
  const values = props.widgetData.data.datasets?.[0]?.data || []

  return labels.map((label, index) => ({
    id: label.id,
    name: label.name,
    value: values[index] ?? 0,
  }))
})
</script>

<template>
  <VCard class="pa-4 d-flex flex-column gap-3">
    <div class="d-flex align-center justify-space-between">
      <div class="d-flex flex-column">
        <div class="text-h5 d-flex align-center gap-2">
          <div
            v-show="widgetData.show_icon && widgetData.icon"
            class="chart-icon-wrapper"
            :style="{ background: widgetData.icon_background }"
          >
            <VIcon :icon="widgetData.icon" size="sm" />
          </div>
          {{ widgetData.name }}
        </div>
      </div>
    </div>

    <div v-if="datasetLabel" class="d-flex justify-end px-3">
      <span class="text-caption text-medium-emphasis">{{ datasetLabel }}</span>
    </div>

    <div v-if="loading" class="audit-list px-3 py-1">
      <div v-for="i in 5" :key="i" class="d-flex align-center justify-space-between py-2.5">
        <VSkeletonLoader type="text" width="65%" class="my-1" />
        <VSkeletonLoader type="text" width="15%" class="my-1" />
      </div>
    </div>
    <VList
      v-else
      lines="one"
      class="audit-list"
    >
      <template v-for="(item, index) in allItems" :key="index">
        <VListItem class="px-3">
          <template #append>
            <span class="font-weight-medium text-body-1">{{ item.value }}</span>
          </template>
          <VListItemTitle class="text-body-1 text-wrap">
            {{ item.name }}
          </VListItemTitle>
        </VListItem>
        <VDivider v-if="index < allItems.length - 1" />
      </template>
    </VList>
  </VCard>
</template>

<style scoped lang="scss">
.audit-list {
  max-height: 240px;
  overflow-y: auto;

  :deep(.v-list-item__title) {
    line-height: 1.4;
  }
}

.chart-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  padding: 4px;
}
</style>
